import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { leadsApi, Lead } from '../../lib/api/leadsApi'
import { getCustomerTypes, CustomerType } from '../../lib/api/customerTypesApi'
import { leadTasksApi } from '../../lib/api/leadTasksApi'
import { leadFollowUpsApi } from '../../lib/api/leadFollowUpsApi'
import { customersApi } from '../../lib/api/customersApi'
import { contactsApi } from '../../lib/api/contactsApi'
import { ConvertLeadDrawer } from '../drawers/ConvertLeadDrawer'
import { ConfirmDialog } from '../shared/ConfirmDialog'
import { type ConvertLeadFormData } from '../../lib/schemas/convertLeadSchema'

interface ConvertLeadFlowProps {
  open: boolean
  lead: Lead | null
  onClose: () => void
  onSuccess: () => void
}

export function ConvertLeadFlow({ open, lead, onClose, onSuccess }: ConvertLeadFlowProps) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [pendingConvertData, setPendingConvertData] = useState<ConvertLeadFormData | null>(null)

  const leadId = lead?.id

  // 1. Fetch tasks and events to check for dependencies
  const { data: tasks = [] } = useQuery({
    queryKey: ['lead-tasks', leadId],
    queryFn: () => leadTasksApi.getAll(leadId!),
    enabled: open && !!leadId,
  })

  const { data: events = [] } = useQuery({
    queryKey: ['lead-follow-ups', leadId],
    queryFn: () => leadFollowUpsApi.getAll(leadId!),
    enabled: open && !!leadId,
  })

  const pendingTasksCount = tasks.filter(t => t.status === 'PENDING' || t.status === 'IN_PROGRESS').length
  const pendingEventsCount = events.filter(f => f.status === 'PENDING').length
  const hasPendingItems = pendingTasksCount > 0 || pendingEventsCount > 0

  // 2. Fetch customer types
  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
    enabled: open,
  })

  // 3. Fetch companies and contacts dropdown options
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customersApi.getAll({ pageSize: 100 }),
    enabled: open,
  })

  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => contactsApi.getAll({ limit: 100 }),
    enabled: open,
  })

  const companyOptions = useMemo(() => {
    return (customersData?.items ?? []).map(c => ({ label: c.companyName || c.name, value: c.id }))
  }, [customersData])

  const contactOptions = useMemo(() => {
    return (contactsData?.items ?? []).map(c => ({ label: c.name, value: c.id }))
  }, [contactsData])

  const enrichedCompanyOptions = useMemo(() => {
    if (!lead) return companyOptions
    const list = [...companyOptions]
    if (lead.companyName) {
      const exists = list.some(o => o.label.toLowerCase() === lead.companyName?.toLowerCase())
      if (!exists) {
        list.unshift({ label: lead.companyName, value: lead.companyName })
      }
    }
    return list
  }, [companyOptions, lead])

  const enrichedContactOptions = useMemo(() => {
    if (!lead) return contactOptions
    const list = [...contactOptions]
    if (lead.name) {
      const exists = list.some(o => o.label.toLowerCase() === lead.name.toLowerCase())
      if (!exists) {
        list.unshift({ label: lead.name, value: lead.name })
      }
    }
    return list
  }, [contactOptions, lead])

  const defaultCompany = useMemo(() => {
    if (!lead || !lead.companyName) return ''
    const match = companyOptions.find(o => o.label.toLowerCase() === lead.companyName?.toLowerCase())
    return match ? match.value : lead.companyName
  }, [companyOptions, lead])

  const defaultContact = useMemo(() => {
    if (!lead || !lead.name) return ''
    const match = contactOptions.find(o => o.label.toLowerCase() === lead.name.toLowerCase())
    return match ? match.value : lead.name
  }, [contactOptions, lead])

  // 4. Mutation for converting lead
  const convertMut = useMutation({
    mutationFn: async (p: ConvertLeadFormData) => {
      // Update customer type on the lead if it changed
      const newCtId = p.customerTypeId
      const oldCtId = lead?.customerTypeId ?? undefined
      if (newCtId !== oldCtId) {
        try {
          await leadsApi.update(leadId!, { customerTypeId: newCtId ?? 0 })
        } catch {
          // If user lacks general update permission, customerTypeId will be set via convert payload below
        }
      }
      return leadsApi.convert(leadId!, {
        companyName: enrichedCompanyOptions.find(o => o.value === p.company)?.label || p.company,
        dealName: p.dealName,
        dealStage: p.dealStage,
        closingDate: p.expectedClosingDate,
        amount: parseFloat(p.amount) || 0,
        description: p.description,
        source: p.source,
        priority: p.priority,
        customerTypeId: p.customerTypeId ?? null,
      })
    },
    onSuccess: (data) => {
      toast.success('Converted!')
      qc.invalidateQueries({ queryKey: ['lead', leadId] })
      qc.invalidateQueries({ queryKey: ['leads-list'] })
      qc.invalidateQueries({ queryKey: ['deals-list'] })
      const dealId = data?.dealId || data?.DealId
      if (dealId) {
        navigate(`/deals/${dealId}`)
      } else {
        toast.warning('Lead converted successfully, but Deal ID was not returned for redirection.')
      }
      onSuccess()
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Conversion failed'),
  })

  const handleSubmit = (data: ConvertLeadFormData) => {
    setPendingConvertData(data)
  }

  if (!lead) return null

  return (
    <>
      <ConvertLeadDrawer
        open={open}
        onClose={onClose}
        onSubmit={handleSubmit}
        isSubmitting={convertMut.isPending}
        initialData={{
          dealName: lead.name || '',
          owner: lead.owner.name,
          company: defaultCompany,
          contact: defaultContact,
          source: lead.source,
          customerTypeId: lead.customerTypeId ?? undefined,
          expectedClosingDate: lead.expectedCloseDate
            ? lead.expectedCloseDate.split('T')[0]   // ISO → YYYY-MM-DD for date input
            : '',
          amount: lead.expectedAmount != null ? String(lead.expectedAmount) : '',
        }}
        companyOptions={enrichedCompanyOptions}
        contactOptions={enrichedContactOptions}
        orgId={lead.orgId}
        customerTypes={customerTypes}
      />

      <ConfirmDialog
        open={!!pendingConvertData}
        title="Convert Lead"
        description={
          hasPendingItems
            ? "There are pending tasks or events/meetings for this lead. Do you want to proceed with the conversion? After conversion, all pending tasks and events will be marked as done."
            : "Are you sure you want to convert this lead to a deal?"
        }
        confirmLabel="Convert"
        confirmColor="primary"
        onClose={() => setPendingConvertData(null)}
        onConfirm={async () => {
          if (pendingConvertData) {
            const data = pendingConvertData
            setPendingConvertData(null)
            await convertMut.mutateAsync(data)
          }
        }}
      />
    </>
  )
}
